import React, { createContext, useRef, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { ClientType, setClient } from '@/api/client-registry';
import { logger } from '@/services/logger';
import { env } from '@/env';
import { loginRequest } from '@/auth/authConfig';

interface ClientContextType {
  authApiClient: AxiosInstance;
  controlPlaneApiClient: AxiosInstance;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

/**
 * Sets up a response interceptor on an axios client to handle 401 errors
 * by attempting token refresh and retrying the request
 * @param client - The axios client instance
 * @param msalInstance - The MSAL public client application instance
 * @param account - The current user account
 * @param scopes - The scopes to request when acquiring a new token
 * @param isRedirectingRef - Ref to track if a redirect is already in progress
 */
function setupResponseInterceptor(
  client: AxiosInstance,
  msalInstance: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
  isRedirectingRef: React.RefObject<boolean>
): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Only handle 401 errors that haven't been retried yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        logger.info('Received 401 error, attempting to refresh token', {
          context: 'ClientContext.responseInterceptor',
          url: originalRequest.url,
        });

        try {
          // Attempt to get a fresh token with forceRefresh
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes,
            account,
            forceRefresh: true,
          });

          logger.info('Token refreshed successfully, retrying request', {
            context: 'ClientContext.responseInterceptor',
            url: originalRequest.url,
          });

          // Update the authorization header with the new token
          originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;

          // Retry the original request with the new token
          return await client(originalRequest);
        } catch (refreshError) {
          logger.warn('Silent token refresh failed', {
            context: 'ClientContext.responseInterceptor',
            error: refreshError instanceof Error ? refreshError.message : String(refreshError),
          });

          // If silent refresh fails and interaction is required, redirect to login
          if (refreshError instanceof InteractionRequiredAuthError) {
            // Prevent multiple concurrent redirects
            if (!isRedirectingRef.current) {
              isRedirectingRef.current = true;

              logger.info('Interactive authentication required, redirecting to login', {
                context: 'ClientContext.responseInterceptor',
              });

              // Trigger interactive login
              await msalInstance.loginRedirect(loginRequest);
            }
          }

          // Re-throw the original error if we can't recover
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Creates and configures an axios client with request and response interceptors
 * for token management
 * @param baseURL - The base URL for the API
 * @param msalInstance - The MSAL public client application instance
 * @param account - The current user account
 * @param scopes - The scopes to request when acquiring tokens
 * @param isRedirectingRef - Ref to track if a redirect is already in progress
 * @returns Configured axios instance
 */
function createApiClient(
  baseURL: string,
  msalInstance: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
  isRedirectingRef: React.RefObject<boolean>
): AxiosInstance {
  const client = axios.create({ baseURL });

  // Add request interceptor to attach token
  client.interceptors.request.use(async (config) => {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes,
      account,
    });

    config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
    return config;
  });

  // Add response interceptor for 401 handling
  setupResponseInterceptor(client, msalInstance, account, scopes, isRedirectingRef);

  return client;
}

/**
 * Provider component that manages axios API clients and token acquisition
 * Handles MSAL token integration and 401 error recovery
 */
export function ClientProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  // Ref to track if a login redirect is already in progress (prevents multiple redirects)
  const isRedirectingRef = useRef(false);

  // Define scopes for each API client
  const authApiScopes = [`api://${env.VITE_AUTH_CLIENT_ID}/access_as_user`, 'profile', 'openid'];
  const controlPlaneScopes = [`api://${env.VITE_DATA_MISSION_CLIENT_ID}/access_as_user`, 'profile', 'openid'];

  // Create API clients - these will be recreated if account changes
  // Since we're inside the authenticated flow, account should always be available
  const authApiClient = createApiClient(env.VITE_AUTH_BASE_URL, instance, account, authApiScopes, isRedirectingRef);

  const controlPlaneApiClient = createApiClient('/api', instance, account, controlPlaneScopes, isRedirectingRef);

  // Register clients synchronously so they're available for child components
  setClient(ClientType.Auth, authApiClient);
  setClient(ClientType.ControlPlane, controlPlaneApiClient);

  return (
    <ClientContext.Provider
      value={{
        authApiClient,
        controlPlaneApiClient,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}
